import React, { useState, useCallback, memo, useMemo } from 'react';
import {
  useCreateGlobalAssemblyMutation,
  useDeleteGlobalAssemblyMutation,
  useFetchAssembliesByFileId,
} from '../../../hooks/useFetchAssembliesByFileId';
import { useSetSelectedAssemblyFromAssemblyPicker } from '../../../hooks/useSetSelectedAssembly';
import {
  Button,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  useTheme,
  alpha,
} from '@mui/material';
import { AssemblyTableProps } from '../../../types/FolderTypes';
import { GlobalAssembly } from '../../../api/protosCompiled/globalAssembly/globalAssembly_pb';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { useHandleMoveItemOrAssemblyFile } from '../folderManager/helpers';
import { useFolderStore } from '../../../stores/folderStore';
import { useHandleGlobalStateState } from '../../../hooks/useHandleGlobalState';
import { ContentCopy, DriveFileMove, Edit, Delete, Visibility } from '@mui/icons-material';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { shallow } from 'zustand/shallow';
import { useUnityBuildStore } from '../../../stores/store';
import { PERMISSIONS, UB_AccountUser, usePermissions } from '../../accessManager';
import { useFetchUser } from '../../../hooks/useFetchUsers';

// Memoized action button component
const ActionButton = memo(
  ({
    onClick,
    icon,
    tooltip,
    theme,
    colorType = 'primary',
  }: {
    onClick: (e: React.MouseEvent) => void;
    icon: React.ReactNode;
    tooltip: string;
    theme: any;
    colorType?: 'primary' | 'error';
  }) => (
    <Tooltip title={tooltip} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          color: theme.palette.text.secondary,
          transition: 'all 0.2s ease',
          '&:hover': {
            color: colorType === 'error' ? theme.palette.error.main : theme.palette.primary.main,
            backgroundColor: alpha(
              colorType === 'error' ? theme.palette.error.main : theme.palette.primary.main,
              0.1
            ),
          },
        }}
        aria-label={tooltip}
      >
        {icon}
      </IconButton>
    </Tooltip>
  )
);
ActionButton.displayName = 'ActionButton';

// Memoized assembly row component
const AssemblyRow = memo(
  ({
    showIcons,
    assembly,
    index,
    theme,
    isCreatingAssembly,
    handleSetSelectedAssembly,
    handleCopy,
    handleGlobalState,
    setStateFunctions,
    moveCurrentAssemblyFile,
    isMoveMode,
    setIsMoveMode,
    handleDeleteClick,
    currentUser,
  }: {
    showIcons: boolean;
    assembly: GlobalAssembly.AsObject;
    index: number;
    theme: any;
    isCreatingAssembly: boolean;
    handleSetSelectedAssembly: (assembly: GlobalAssembly.AsObject) => void;
    handleCopy: (assembly: GlobalAssembly.AsObject) => void;
    handleGlobalState: any;
    setStateFunctions: any;
    moveCurrentAssemblyFile: any;
    isMoveMode: boolean;
    setIsMoveMode: (mode: boolean) => void;
    handleDeleteClick: (assembly: GlobalAssembly.AsObject) => void;
    currentUser: UB_AccountUser.AsObject;
  }) => {
    const bgColor =
      theme.palette.mode === 'dark'
        ? index % 2 === 0
          ? '#1e1e1e' // Even rows in dark mode (darker)
          : '#2c2c2c' // Odd rows in dark mode (lighter gray)
        : index % 2 === 0
          ? 'transparent' // Even rows in light mode
          : alpha(theme.palette.action.hover, 0.05); // Odd rows in light mode

    const { hasAnyPermission, hasPermission } = usePermissions(currentUser);
    // Memoized event handlers
    const handleRowClick = useCallback(
      (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (!(e.target as HTMLElement).closest('button')) {
          e.stopPropagation();
          e.preventDefault();
          if (!isCreatingAssembly) {
            handleSetSelectedAssembly(assembly);
          }
        }
      },
      [assembly, isCreatingAssembly, handleSetSelectedAssembly]
    );

    const handleCopyClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleCopy(assembly);
      },
      [assembly, handleCopy]
    );

    const handleEditClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleGlobalState({
          resetOption: 'resetAssembly',
          setStateFunctions,
        });
      },
      [handleGlobalState, setStateFunctions]
    );

    const handleMoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        moveCurrentAssemblyFile({
          e,
          assembly,
          entitytype: 'assembly',
        });
      },
      [assembly, moveCurrentAssemblyFile]
    );

    const handleCancelMove = useCallback(() => {
      setIsMoveMode(false);
    }, [setIsMoveMode]);

    const handleDeleteClickEvent = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleDeleteClick(assembly);
      },
      [assembly, handleDeleteClick]
    );

    return (
      <TableRow
        hover
        key={assembly.assemblyid}
        onClick={handleRowClick}
        sx={{
          cursor: 'pointer',
          backgroundColor: `${bgColor} !important`,
          color: theme.palette.text.primary,
          transition: 'background-color 0.2s ease',
          '&.MuiTableRow-hover:hover': {
            backgroundColor: `${
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.05)
            } !important`,
          },
          borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
          width: '100%',
        }}
      >
        <TableCell
          sx={{
            color: theme.palette.text.primary,
            borderBottom: 'none',
            width: '70%',
          }}
        >
          {assembly.assemblyname}
        </TableCell>
        <TableCell
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'right',
            borderBottom: 'none',
            width: '30%',
          }}
        >
          {hasAnyPermission([
            PERMISSIONS.CREATE_GLOBAL_ASSEMBLIES,
            PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES,
            PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES,
            PERMISSIONS.DELETE_GLOBAL_ASSEMBLIES,
          ]) ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: 'nowrap',
                gap: '4px',
                justifyContent: 'flex-end',
              }}
            >
              {showIcons && hasPermission(PERMISSIONS.CREATE_GLOBAL_ASSEMBLIES) && (
                <ActionButton
                  onClick={handleCopyClick}
                  icon={<ContentCopy fontSize="small" />}
                  tooltip="Copy assembly"
                  theme={theme}
                />
              )}

              {showIcons && hasPermission(PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES) ? (
                <ActionButton
                  onClick={handleEditClick}
                  icon={<Edit fontSize="small" />}
                  tooltip="Edit assembly"
                  theme={theme}
                />
              ) : (
                <ActionButton
                  onClick={handleEditClick}
                  icon={<Visibility fontSize="small" />}
                  tooltip="View assembly"
                  theme={theme}
                />
              )}
              {showIcons &&
                hasPermission(PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES) &&
                (!isMoveMode ? (
                  <ActionButton
                    onClick={handleMoveClick}
                    icon={<DriveFileMove fontSize="small" />}
                    tooltip="Move assembly to another location"
                    theme={theme}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelMove}
                    type="reset"
                    sx={{
                      minWidth: '32px',
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    aria-label="Cancel move"
                  >
                    Cancel
                  </Button>
                ))}
              {showIcons && hasPermission(PERMISSIONS.DELETE_GLOBAL_ASSEMBLIES) ? (
                <ActionButton
                  onClick={handleDeleteClickEvent}
                  icon={<Delete fontSize="small" />}
                  tooltip="Delete assembly and its contents"
                  theme={theme}
                  colorType="error"
                />
              ) : null}
            </Stack>
          ) : hasPermission(PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES) ? (
            <ActionButton
              onClick={handleEditClick}
              icon={<Visibility fontSize="small" />}
              tooltip="View Assembly"
              theme={theme}
            />
          ) : null}
        </TableCell>
      </TableRow>
    );
  }
);
AssemblyRow.displayName = 'AssemblyRow';

function AssemblyTable({ context, folder }: AssemblyTableProps) {
  const theme = useTheme();

  const showIcons = context === 'primary' ? true : false;

  // Extract only the specific state values needed from stores using selectors with shallow equality
  const isMoveMode = useStoreWithEqualityFn(useFolderStore, state => state.isMoveMode, shallow);
  const setIsMoveMode = useStoreWithEqualityFn(
    useFolderStore,
    state => state.setIsMoveMode,
    shallow
  );

  // Group related state from global item store
  const { setIsViewingItemForm, setIsCreatingItem } = useStoreWithEqualityFn(
    useGlobalItemStore,
    state => ({
      setIsViewingItemForm: state.setIsViewingItemForm,
      setIsCreatingItem: state.setIsCreatingItem,
    }),
    shallow
  );

  // Group related state from assembly store
  const {
    isCreatingAssembly,
    setIsCreatingAssembly,
    setIsEditMode,
    setIsViewingAssemblyForm,
    setAssemblyOption,
    setCurrentGlobalAssembly,
  } = useStoreWithEqualityFn(
    useAssemblyStore,
    state => ({
      isCreatingAssembly: state.isCreatingAssembly,
      setIsCreatingAssembly: state.setIsCreatingAssembly,
      setIsEditMode: state.setIsEditMode,
      setIsViewingAssemblyForm: state.setIsViewingAssemblyForm,
      setAssemblyOption: state.setAssemblyOption,
      setCurrentGlobalAssembly: state.setCurrentGlobalAssembly,
    }),
    shallow
  );

  const moveCurrentAssemblyFile = useHandleMoveItemOrAssemblyFile();
  const handleGlobalState = useHandleGlobalStateState();
  const setSelectedAssembly = useSetSelectedAssemblyFromAssemblyPicker();

  // Fetch data
  const { data: globalAssemblies, isLoading } = useFetchAssembliesByFileId(folder.folderid);
  const { mutate: createGlobalAssembly } = useCreateGlobalAssemblyMutation();
  const { mutate: deleteGlobalAssembly } = useDeleteGlobalAssemblyMutation();
  const { data: currentUser } = useFetchUser();

  // Local state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assemblyToDelete, setAssemblyToDelete] = useState<GlobalAssembly.AsObject | null>(null);

  // Memoized handlers
  const handleSetSelectedAssembly = useCallback(
    (assembly: GlobalAssembly.AsObject) => {
      // Set flag to indicate an active run is in progress
      // This ensures Point Editor shows "Complete Run" immediately
      useUnityBuildStore.getState().setHasActiveRun(true);
      setSelectedAssembly({
        globalAssembly: assembly,
      });
    },
    [setSelectedAssembly]
  );

  const handleDeleteClick = useCallback((assembly: GlobalAssembly.AsObject) => {
    setAssemblyToDelete(assembly);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (assemblyToDelete) {
      deleteGlobalAssembly(assemblyToDelete);
      setIsMoveMode(false);
      setDeleteDialogOpen(false);
      setAssemblyToDelete(null);
    }
  }, [assemblyToDelete, deleteGlobalAssembly, setIsMoveMode]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setAssemblyToDelete(null);
  }, []);

  const handleCopy = useCallback(
    (assembly: GlobalAssembly.AsObject) => {
      createGlobalAssembly(assembly);
      setIsMoveMode(false);
    },
    [createGlobalAssembly, setIsMoveMode]
  );

  // Memoize state update functions for the edit handler
  const setStateFunctions = useMemo(
    () => [
      {
        setStateFunction: setCurrentGlobalAssembly,
        prop: null,
      },
      {
        setStateFunction: setCurrentGlobalAssembly,
        prop: null, // Will be updated with assembly in the component
      },
      {
        setStateFunction: setIsViewingAssemblyForm,
        prop: true,
      },
      {
        setStateFunction: setIsViewingItemForm,
        prop: false,
      },
      {
        setStateFunction: setIsCreatingItem,
        prop: false,
      },
      {
        setStateFunction: setAssemblyOption,
        prop: 'global',
      },
      {
        setStateFunction: setIsCreatingAssembly,
        prop: false,
      },
      {
        setStateFunction: setIsEditMode,
        prop: true,
      },
    ],
    [
      setCurrentGlobalAssembly,
      setIsViewingAssemblyForm,
      setIsViewingItemForm,
      setIsCreatingItem,
      setAssemblyOption,
      setIsCreatingAssembly,
      setIsEditMode,
    ]
  );

  // Loading state
  if (!globalAssemblies || isLoading) {
    return <CircularProgress size={24} sx={{ display: 'block', margin: '0 auto' }} />;
  }

  // If there are no assemblies, don't render anything
  if (globalAssemblies.length === 0) {
    return null;
  }

  return (
    <>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth: '100%',
          boxSizing: 'border-box',
          backgroundColor: 'transparent',
          '& .MuiTableRow-root': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <TableBody>
          {globalAssemblies.map((assembly, index) => (
            <AssemblyRow
              key={assembly.assemblyid}
              assembly={assembly}
              index={index}
              theme={theme}
              isCreatingAssembly={isCreatingAssembly}
              handleSetSelectedAssembly={handleSetSelectedAssembly}
              handleCopy={handleCopy}
              handleGlobalState={handleGlobalState}
              setStateFunctions={[
                ...setStateFunctions.slice(0, 1),
                {
                  setStateFunction: setCurrentGlobalAssembly,
                  prop: assembly,
                },
                ...setStateFunctions.slice(2),
              ]}
              moveCurrentAssemblyFile={moveCurrentAssemblyFile}
              isMoveMode={isMoveMode}
              setIsMoveMode={setIsMoveMode}
              handleDeleteClick={handleDeleteClick}
              showIcons={showIcons}
              currentUser={currentUser || ({} as UB_AccountUser.AsObject)}
            />
          ))}
        </TableBody>
      </Table>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Assembly"
        message={`Are you sure you want to delete the assembly "${assemblyToDelete?.assemblyname}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
      />
    </>
  );
}

export default React.memo(AssemblyTable);
